import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { GetVoicesParams, PaginatedResponse, Pagination, Voices, typicodeApi } from 'shared/api';

type TVoiceState = {
  voiceList: Voices[];
  selectedVoice: Voices | null;
  pagination: Pagination | null;
};

const initialState: TVoiceState = {
  voiceList: [],
  selectedVoice: null,
  pagination: null,
};

export const voiceModel = createSlice({
  name: 'voice',
  initialState,
  reducers: {
    setSelectedVoice: (state, { payload }: PayloadAction<Voices | null>) => {
      state.selectedVoice = payload;
    },
    setVoiceList: (state, { payload }: PayloadAction<Voices[]>) => {
      state.voiceList = payload;
    },
    setPagination: (state, { payload }: PayloadAction<Pagination>) => {
      state.pagination = payload;
    },
  },
});

export const { setSelectedVoice } = voiceModel.actions;

// react-query actions
const VOICES_QUERY_KEY = 'voices';

export const getVoicesAsync = (params: GetVoicesParams) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<Voices>>>(
    [VOICES_QUERY_KEY, params],
    () => typicodeApi.voices.getVoices(params),
    {
      onSuccess: ({ data }) => {
        dispatch(voiceModel.actions.setVoiceList(data.data));
        dispatch(voiceModel.actions.setPagination(data.pagination));
      },
      onError: () => {},
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

const VOICE_BY_ID_QUERY_KEY = 'voiceById';
export const getVoiceByIdAsync = (id: string) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<Voices>>(VOICE_BY_ID_QUERY_KEY, () => typicodeApi.voices.getVoicesById(id), {
    onSuccess: ({ data }) => {
      dispatch(voiceModel.actions.setSelectedVoice(data));
    },
    onError: () => {},
    refetchOnWindowFocus: false,
  });

// mutations
export const createVoiceAsync = () => {
  const queryClient = useQueryClient();
  return useMutation((data: Omit<Voices, '_id'>) => typicodeApi.voices.postVoices(data), {
    onSuccess: () => {
      toast.success('Voice created successfully!');
      void queryClient.invalidateQueries(VOICES_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error creating voice. Try again!');
    },
  });
};

export const updateVoiceAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: Partial<Voices> }) => typicodeApi.voices.patchVoices(id, data),
    {
      onSuccess: () => {
        toast.success('Voice updated successfully!');
        void queryClient.invalidateQueries(VOICES_QUERY_KEY);
      },
      onError: () => {
        toast.error('Error updating voice. Try again!');
      },
    },
  );
};

export const deleteVoiceAsync = () => {
  const queryClient = useQueryClient();
  return useMutation((id: string) => typicodeApi.voices.deleteVoicesById(id), {
    onSuccess: () => {
      toast.success('Voice deleted successfully!');
      void queryClient.invalidateQueries(VOICES_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error deleting voice. Try again!');
    },
  });
};

// selectors
export const useVoice = () => useSelector((state: RootState) => state.voices);

export const reducer = voiceModel.reducer;
